/**
 * What is running, and which schema it was released against.
 *
 * An operator needs this when things are broken, which is why the version
 * endpoint is reachable without a decision at all. The release log is the
 * fuller record: the commit, the environment, and the migration head the code
 * was promoted with — the two travel together as one artifact.
 *
 * The log only grows, and it is read a page at a time. A table that stopped at
 * the page boundary and said nothing told an operator that the oldest release
 * shown was the oldest there is — which, during an incident, is exactly the
 * kind of quiet falsehood that sends somebody looking in the wrong place.
 */

import { useCallback } from "react";
import { api } from "../api/client";
import type { Page, ReleaseRecord } from "../api/types";
import { usePagedCollection } from "../api/useCollection";
import { Card, Identifier, Mono, More, when } from "../components/primitives";
import { Empty, Show } from "../components/states";

/**
 * The page size this screen has always asked for.
 *
 * Unchanged on purpose. What changes is not how much is read at once but
 * whether the fifty-first release is reachable at all: it used to be absent
 * without the screen ever saying so.
 */
const PAGE = 50;

export function Releases() {
  /**
   * The paging is the shared primitive's; the path and the page size are this
   * screen's business.
   *
   * The primitive reads pages as `PageOf`, which is narrower than the `Page`
   * the API returns: it does not carry `projection`. Nothing is discarded by
   * that here, and the proof is worth writing down because the next reader will
   * ask. `/platform/releases` answers from `ReleaseLogRepository.page`, which
   * returns `Page.of(...)`; `Page.of` never sets `projection` and the field
   * defaults to `None`; and the only code in the platform that ever constructs
   * a `PageProjection` is the outbox service, which does not serve this route.
   * So `projection` is null on every page this screen reads. If the release log
   * ever starts answering for its own completeness, this is the line that would
   * have to stop throwing it away.
   */
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<ReleaseRecord>>("/platform/releases", {
        params: { limit: PAGE, cursor },
        signal,
      }),
    [],
  );
  const releases = usePagedCollection<ReleaseRecord>(readPage);

  return (
    <Card title="Release log" bodyless>
      <Show
        resource={releases.state}
        what="the release log"
        onRetry={releases.refresh}
        empty={() =>
          releases.items.length === 0 ? (
            <Empty
              title="No release recorded."
              hint="The deploy sequence records one; a deployment that has never been promoted has nothing here."
            />
          ) : null
        }
      >
        {() => (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <caption className="nx-visually-hidden">Releases promoted to this deployment</caption>
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Environment</th>
                  <th scope="col">Commit</th>
                  <th scope="col">Migration head</th>
                  <th scope="col">Recorded</th>
                </tr>
              </thead>
              <tbody>
                {releases.items.map((release) => (
                  <tr key={release.release_id}>
                    <td className="nx-table__primary">{release.version}</td>
                    <td>{release.environment}</td>
                    <td>
                      <Identifier value={release.commit} />
                    </td>
                    <td>
                      <Mono>{release.migration_head}</Mono>
                    </td>
                    <td>
                      {when(release.recorded_at)}
                      <span className="nx-hint" style={{ display: "block" }}>
                        {release.recorded_by}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Show>
      <More
        collection={releases}
        label="Show earlier releases"
        hint="This is one page of the release log, newest first — not the whole deploy history."
        placement="bodyless"
      />
    </Card>
  );
}
